import React, { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Drawer — Right-side slide-in panel on desktop, bottom sheet on mobile.
 * Matches the "premium drawer" UX used by the Receipt Dashboard's
 * create/open-receipt flow.
 *
 * Props:
 *   open              boolean   — visibility flag (when false, renders nothing)
 *   onClose           function  — called when user clicks the backdrop
 *   size              "sm" | "md" | "lg" | "xl"  — drawer width on desktop (default "md")
 *                                  sm: 460/520px      — confirmations, tiny forms
 *                                  md: 520/580px      — standard forms (default)
 *                                  lg: 600/680px      — wider forms with lists
 *                                  xl: 680/760/860px+ — detail views (invoices, etc.)
 *   zIndex            number    — z-index for the backdrop (default 100). Use higher
 *                                  (e.g. 9999) only if the drawer must stack above
 *                                  other portaled UI.
 *   closeOnBackdrop   boolean   — whether clicking the backdrop fires onClose
 *                                  (default true). Pass false to lock the drawer
 *                                  open while an in-progress action is running.
 *   showDragHandle    boolean   — show the mobile drag handle (default true)
 *   children          ReactNode — drawer contents (header, body, footer — your call)
 *   className         string    — additional classes appended to the panel
 *
 * Notes:
 * - Renders via React Portal into document.body to escape parent stacking contexts.
 * - The panel uses `overflow-y-auto` so any content can scroll within it. If you
 *   need a sticky header/footer with a scrolling middle section, give your inner
 *   content `flex-shrink-0` / `flex-1 overflow-y-auto` accordingly and the drawer
 *   will respect it.
 */
const SIZE_CLASSES = {
  sm: "sm:w-[460px] md:w-[520px]",
  md: "sm:w-[520px] md:w-[580px]",
  lg: "sm:w-[600px] md:w-[680px]",
  xl: "sm:w-[680px] md:w-[760px] lg:w-[860px] xl:w-[920px]",
  full: "sm:w-[88%] lg:w-[80%] xl:w-[74%] 2xl:max-w-[1500px]",
};

// Keyframe styles. Injected once globally (idempotent — re-injecting the same
// rules with the same IDs is harmless and only happens once per page load).
const DRAWER_STYLE_ID = "tab-drawer-keyframes";
const DRAWER_KEYFRAMES = `
  @keyframes tabDrawerSlideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
  @keyframes tabDrawerBackdropFade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .tab-drawer-backdrop { animation: tabDrawerBackdropFade 180ms ease-out; }
  @media (min-width: 640px) {
    .tab-drawer-panel { animation: tabDrawerSlideIn 240ms cubic-bezier(0.32, 0.72, 0, 1); }
  }
`;

const ensureDrawerStyles = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(DRAWER_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = DRAWER_STYLE_ID;
  style.textContent = DRAWER_KEYFRAMES;
  document.head.appendChild(style);
};

const Drawer = ({
  open,
  onClose,
  size = "md",
  zIndex = 100,
  closeOnBackdrop = true,
  showDragHandle = true,
  children,
  className = "",
}) => {
  useEffect(() => {
    if (open) ensureDrawerStyles();
  }, [open]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const widthClasses = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  return createPortal(
    <div
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-hidden sm:flex sm:items-stretch sm:justify-end tab-drawer-backdrop"
      style={{ touchAction: "none", zIndex }}
      onClick={(e) => {
        if (e.target === e.currentTarget && closeOnBackdrop) onClose?.();
      }}
    >
      <div
        className={`absolute inset-x-0 bottom-0 sm:static sm:inset-auto bg-white rounded-t-3xl sm:rounded-none border-t sm:border-t-0 sm:border-l border-slate-200 sm:border-slate-300 w-full ${widthClasses} max-h-[calc(100vh-env(safe-area-inset-top,0px)-12px)] sm:max-h-screen sm:h-screen flex flex-col shadow-2xl overflow-y-auto tab-drawer-panel ${className}`}
        style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
        onClick={(e) => e.stopPropagation()}
      >
        {showDragHandle && (
          <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-slate-300" />
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Drawer;